"use client";
import IframeDialog from "@/app/[locale]/(user-page)/report/components/Dialog/IframeDialog";
import { APP_ROUTES } from "@/constants/routes";
import { useAppSelector } from "@/lib/hooks";
import { ConfigData, MethodConfig } from "@/models/config";
import { subscription } from "@/models/subscription";
import "@/styles/Banner/style.css";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useMemo, useState } from "react";

interface BannerProps {
  pathName?: string | null;
  userServer?: any;
  isLoggedIn?: boolean;
  isMobile?: boolean;
}

const Banner = (props: BannerProps) => {
  const { userServer, isLoggedIn, isMobile } = props;
  const queryClient = useQueryClient();

  const config = queryClient.getQueryData<ConfigData>(["config"]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const t = useTranslations("messages");
  const { user: userStore } = useAppSelector((state) => state.auth);
  const user = queryClient.getQueryData(["user"]);
  const subscription = queryClient.getQueryData<subscription>([
    "subscription-trial",
  ]);
  const locale = useLocale();
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };
  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };
  const tranferUrl = useMemo((): string => {
    const parseUrl = JSON.parse(config?.viewDemoLink?.url || '""');
    switch (config?.viewDemoLink?.method) {
      case MethodConfig.YOUTUBE:
        return parseUrl[locale]
          ? parseUrl[locale]
            .replace(/^"|"$/g, "")
            .replace("watch?v=", "embed/")
            .concat("?autoplay=1&vq=hd1080")
          : parseUrl["en"]
            .replace(/^"|"$/g, "")
            .replace("watch?v=", "embed/")
            .concat("?autoplay=1&vq=hd1080") || "";
      case MethodConfig.SUPADEMO:
        return parseUrl[locale]
          ? parseUrl[locale]?.replace(/^"|"$/g, "")
          : parseUrl["en"]?.replace(/^"|"$/g, "") || "";
      default:
        return "";
    }
  }, [config?.viewDemoLink, locale]);

  const tranferUrlMobile = useMemo((): string => {
    const parseUrl = JSON.parse(config?.viewDemoLink?.url || '""');
    const mobileLocale = "mobile" + locale;
    switch (config?.viewDemoLink?.method) {
      case MethodConfig.YOUTUBE:
        return parseUrl[mobileLocale]
          ? parseUrl[mobileLocale]
            ?.replace(/^"|"$/g, "")
            ?.replace("watch?v=", "embed/")
            .concat("?autoplay=1&vq=hd1080")
          : parseUrl["mobileen"]
            ?.replace(/^"|"$/g, "")
            ?.replace("watch?v=", "embed/")
            .concat("?autoplay=1&vq=hd1080") || "";
      case MethodConfig.SUPADEMO:
        return parseUrl[mobileLocale]
          ? parseUrl[mobileLocale]?.replace(/^"|"$/g, "")
          : parseUrl["mobileen"]?.replace(/^"|"$/g, "") || "";
      default:
        return "";
    }
  }, [config?.viewDemoLink, locale]);

  const buttonTrialSubscription = useMemo(() => {
    if (!subscription?.id) {
      return (
        <Link
          href={APP_ROUTES(locale).PRICING.to}
          className="bg-[#9dc42f] cursor-pointer text-white px-[1.5rem] py-[0.75rem] rounded no-underline font-semibold transition-all duration-200 hover:bg-[#82a525] text-center report-banner-pricing-link"
          translation-key="common_subscribe_now"
        >
          {t("common_subscribe_now")}
        </Link>
      );
    }
    return (
      <Link
        href={APP_ROUTES(locale).TRIAL.to}
        className="bg-[#9dc42f] cursor-pointer text-white px-[1.5rem] py-[0.75rem] rounded no-underline font-semibold transition-all duration-200 hover:bg-[#82a525] text-center report-go-to-trial-link"
        translation-key="banner_primary_action"
      >
        {t("banner_primary_action")}
      </Link>
    );
  }, [subscription, t]);

  if (userServer || userStore) {
    return null;
  }
  if (user || isLoggedIn) {
    return null;
  }
  return (
    <Box
      component="section"
      className="pt-[1rem] lg:pt-[3rem] pr-[5%] pb-[1rem] sm:pb-[2rem] pl-[5%] bg-gradient-to-br from-[#f7faea] to-white"
    >
      <Box className="flex justify-center items-center mx-auto lg:max-w-[800px]">
        <div className="w-full">
          <h1
            className={clsx(" mb-[1.5rem] text-[#0f172a] text-center", {
              "text-[1.53rem] md:text-[2.5rem] lg:!text-[3rem] leading-[1.2]":
                locale === "en",
              "text-[1.38rem] md:text-[2.25rem] lg:!text-[2.5rem] leading-[1.3]":
                locale === "vi",
            })}
          >
            <span className="text-[#010101]" translation-key="banner_title">
              {t("banner_title")}
            </span>
            <span
              className="text-[#1f61a9] block"
              translation-key="banner_subtitle"
            >
              {t("banner_subtitle")}
            </span>
          </h1>
          <p
            className="text-[1.1rem] lg:!text-[1.25rem] text-[#475569] mb-[2rem] leaading-[1.7] text-center"
            translation-key="banner_description"
          >
            {t("banner_description")}
          </p>
          <div className="flex-col sm:flex-row items-stretch lg:items-center flex gap-[1.5rem] mb-[2rem] justify-center">
            {buttonTrialSubscription}
            {tranferUrl && (
              <a
                onClick={handleDialogOpen}
                className="bg-[#116bb1] hidden sm:flex text-white cursor-pointer px-[1.5rem] py-[0.75rem] rounded no-underline font-semibold transition-all duration-200 hover:bg-[#0d5a94] text-center"
                translation-key="banner_secondary_action"
              >
                {t("banner_secondary_action")}
              </a>
            )}
            {tranferUrlMobile && (
              <a
                href={tranferUrlMobile}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#116bb1] justify-center items-center flex sm:hidden text-white cursor-pointer px-[1.5rem] py-[0.75rem] rounded no-underline font-semibold transition-all duration-200 hover:bg-[#0d5a94] text-center"
                translation-key="banner_secondary_action"
              >
                {t("banner_secondary_action")}
              </a>
            )}
          </div>
          <div
            translation-key="banner_trust"
            className="flex items-center gap-[0.5rem] text-[#64748b] text-[0.9rem] justify-center"
          >
            <svg
              className="text-[#22c55e] flex-shrink-0"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            {t("banner_trust")}
          </div>
        </div>
      </Box>
      <IframeDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        iframe={tranferUrl}
        title={t("common_view_demo")}
        type={config?.viewDemoLink?.method}
      />
    </Box>
  );
};

export default Banner;
